import { Accordion, AccordionDetails, AccordionSummary, AppBar, Autocomplete, Box, Button, CircularProgress, Divider, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, styled, Switch, TextField, Toolbar, Tooltip, Typography } from '@mui/material';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import TuneIcon from '@mui/icons-material/Tune';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PolicySimulator from '../../../utils/PolicySimulator.ts';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import CustomProfilesPolicySimulator from './components/CustomProfilesPolicySimulator.js';
import { getSimulatorProfiles, setSimulatorProfiles } from '../../../redux/simulatorProfilesSlice.js';
import { useDispatch, useSelector } from 'react-redux';
import findSubjectAttrToSimulator from '../../../utils/findSubjectAttrToSimulator.js';
import { getProfile } from '../../../redux/iaamSlice.js';
import { useNodes, useReactFlow } from '@xyflow/react';
import { setSnackbarMessage } from '../../../redux/snackbarMessageSlice.js';
import sortedProfilesAttributes from '../../../utils/sortedProfilesAttributes.js';
import UserProfileManagement from '../Regulatory/components/UserProfileManagement.js';
import { v4 as uuidv4 } from 'uuid';
import { dataTypes } from '../../../utils/configDataTypes.js';
import { getSubjectSchema } from '../../../redux/subjectSchemaSlice.js';
import { LoadingButton } from '@mui/lab';
import { getSelectedProfileSimulate, setSelectedProfileSimulate } from '../../../redux/selectedProfileSimulateSlice.js';
import DataGridPolicySimulator from './components/DataGridPolicySimulator.js';
import { getOpenSimulatorPolicy } from '../../../redux/openSimulatorPolicySlice.js';
import { getRuleByPolicyDiagram } from '../../../utils/convertPolicyStructure.js';
import dayjs from "dayjs";
import { getSelectedProfilesPolicySimulator } from '../../../redux/selectedProfilesPolicySimulator.js';
import DataGridApplication from './components/DataGridApplication.js';
import DataGridRESTfulAPI from './components/DataGridRESTfulAPI.js';
import DataGridData from './components/DataGridData.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// const iaam = window.iaam;

const getProfileOptionId = (option) => {
    if (option === undefined || option === null) {
        return '';
    }

    if (typeof option === 'string' || typeof option === 'number') {
        return String(option);
    }

    return String(option?.id || option?.profile_id || '');
};
const POLICY_PROFILE_BUILDER_STORAGE_KEY = 'policy-simulation-profile-builder';
const POLICY_PROFILE_SIM_RESULT_STORAGE_KEY = 'policy-simulation-profile-results';
const PROFILE_BUILDER_CHANGED_EVENT = 'policy-simulation-profile-builder-changed';
const DATE_TIME_DISPLAY_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const formatDateTimeDisplay = (value) => {
    if (value === undefined || value === null) {
        return '-';
    }

    const raw = String(value).trim();
    if (!raw || raw === '-') {
        return '-';
    }

    const normalizedIso = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})(?:\.\d+)?(?:Z)?$/);
    if (normalizedIso) {
        return `${normalizedIso[1]} ${normalizedIso[2]}`;
    }

    const parsed = dayjs(raw);
    if (parsed.isValid()) {
        return parsed.format(DATE_TIME_DISPLAY_FORMAT);
    }

    // Normalize only ISO-like datetime strings; keep plain text labels unchanged (e.g. "Tue.").
    const isoLikeDateTime = raw.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2})?)(?:\.\d+)?Z?$/);
    if (isoLikeDateTime) {
        return `${isoLikeDateTime[1]} ${isoLikeDateTime[2]}`;
    }

    return raw;
};

const normalizeReasonList = (value) => {
    if (Array.isArray(value)) {
        return value
            .flatMap((item) => (Array.isArray(item) ? item : [item]))
            .filter(Boolean)
            .map((item) => {
                if (item && typeof item === 'object' && !Array.isArray(item)) {
                    const type = String(item?.type || '').trim();
                    const message = String(item?.message || '').trim();
                    if (!type && !message) {
                        return null;
                    }
                    return {
                        ...item,
                        type,
                        message,
                    };
                }

                const text = String(item || '').trim();
                if (!text) {
                    return null;
                }

                return {
                    type: text,
                    message: '',
                };
            })
            .filter(Boolean);
    }

    if (typeof value === 'string') {
        const raw = value.trim();
        if (!raw || raw === '[]') {
            return [];
        }

        if (raw.includes('[object Object]')) {
            return [];
        }

        try {
            const parsed = JSON.parse(raw);
            return normalizeReasonList(parsed);
        } catch (error) {
            return raw
                .split(',')
                .map((item) => String(item || '').trim())
                .filter(Boolean)
                .map((item) => ({
                    type: item,
                    message: '',
                }));
        }
    }

    return [];
};

const PolicySimulatorPanel = (props) => {
    const { onClose, nodes } = props;

    const dispatch = useDispatch();

    // const use_nodes = useNodes();
    const { getNodes, getEdges } = useReactFlow();
    // const nodes = useNodes();

    const simulatorProfiles = useSelector(getSimulatorProfiles);
    const subjectSchema = useSelector(getSubjectSchema);
    const selectedProfileSimulate = useSelector(getSelectedProfileSimulate);
    const openSimulatorPolicy = useSelector(getOpenSimulatorPolicy);
    const selectedProfiles = useSelector(getSelectedProfilesPolicySimulator);
    const iaamProfile = useSelector(getProfile);

    const [profile, setProfile] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [policySimulator, setPolicySimulator] = useState(null);
    const [isProfileChanged, setIsProfileChanged] = useState(false);
    const [project, setProject] = useState();
    const [selectedEditProfile, setSelectedEditProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openManagement, setOpenManagement] = useState(false);
    const [rows, setRows] = useState([]);
    const [memoizedAttributes, setMemoizedAttributes] = useState({});
    const [disabledRunSimulate, setDisabledRunSimulate] = useState(false);
    const [applicationRows, setApplicationRows] = useState([]);
    const [restfulApiRows, setRestfulApiRows] = useState([]);
    const [dataRows, setDataRows] = useState([]);
    const [isLoadingTable, setIsLoadingTable] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        application: true,
        restfulApi: true,
        data: true,
    });

    const handleAccordionChange = (section) => (_, isExpanded) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: isExpanded,
        }));
    };

    useEffect(() => {
        const file = JSON.parse(localStorage.getItem('policy-file'));
        // const initialProfile = async () => {
        //     setProfile(await iaam.getProfile());
        // }
        if (file) {
            setPolicySimulator(new PolicySimulator(file))
            // initialProfile();
        }
        const project = JSON.parse(localStorage.getItem('project'));
        if (project) {
            setProject(project);
        }
    }, [])

    useEffect(() => {
        if (iaamProfile) {
            setProfile(iaamProfile);
        }
    }, [iaamProfile]);

    useEffect(() => {
        if (!nodes || nodes.length === 0) {
            return;
        }
        if (simulatorProfiles && simulatorProfiles.length > 0) {
            // console.log("simulatorProfiles", simulatorProfiles)
            const usedSubjectAttributes = findSubjectAttrToSimulator(nodes);
            const profilesData = simulatorProfiles.map(profile => {
                let attributes = {};
                Object.keys(usedSubjectAttributes).forEach(key => {
                    attributes[key] = profile?.attributes[key] || null;
                });
                return {
                    ...profile,
                    attributes: attributes,
                };
            });
            setProfiles(profilesData);
        }
    }, [simulatorProfiles, nodes]);

    const selectedProfileId = useMemo(() => getProfileOptionId(selectedProfileSimulate), [selectedProfileSimulate]);

    const selectedProfile = useMemo(() => {
        if (!Array.isArray(selectedProfiles) || selectedProfiles.length === 0) {
            return null;
        }

        const matched = selectedProfiles.find((option) => getProfileOptionId(option) === selectedProfileId);
        return matched || selectedProfiles[0] || null;
    }, [selectedProfiles, selectedProfileId]);

    useEffect(() => {
        if (!Array.isArray(selectedProfiles) || selectedProfiles.length === 0) {
            if (selectedProfileSimulate !== null) {
                dispatch(setSelectedProfileSimulate(null));
            }
            return;
        }

        const hasSelectedProfileId = Boolean(selectedProfileId);
        if (!hasSelectedProfileId) {
            const firstProfileId = getProfileOptionId(selectedProfiles[0]);
            if (firstProfileId) {
                dispatch(setSelectedProfileSimulate({ id: firstProfileId }));
            }
            return;
        }

        const isIdInOptions = selectedProfiles.some(
            (option) => getProfileOptionId(option) === selectedProfileId
        );

        if (!isIdInOptions) {
            const firstProfileId = getProfileOptionId(selectedProfiles[0]);
            if (firstProfileId) {
                dispatch(setSelectedProfileSimulate({ id: firstProfileId }));
            }
            return;
        }

        const isIdOnlyObject = (
            selectedProfileSimulate
            && typeof selectedProfileSimulate === 'object'
            && !Array.isArray(selectedProfileSimulate)
            && Object.keys(selectedProfileSimulate).length === 1
            && selectedProfileSimulate.id === selectedProfileId
        );

        if (!isIdOnlyObject) {
            dispatch(setSelectedProfileSimulate({ id: selectedProfileId }));
        }
    }, [selectedProfiles, selectedProfileId, selectedProfileSimulate, dispatch]);

    const getCurrentPolicyDiagramId = useCallback(() => {
        const policyFile = JSON.parse(localStorage.getItem('policy-file') || '{}');
        return String(policyFile?.policy_diagram_id || '');
    }, []);

    const readPolicySimulationResultStore = useCallback(() => {
        try {
            const rawStore = localStorage.getItem(POLICY_PROFILE_SIM_RESULT_STORAGE_KEY);
            if (!rawStore) {
                return {};
            }

            const parsedStore = JSON.parse(rawStore);
            if (!parsedStore || typeof parsedStore !== 'object' || Array.isArray(parsedStore)) {
                return {};
            }

            return parsedStore;
        } catch (error) {
            return {};
        }
    }, []);

    const writePolicySimulationResultStore = useCallback((store) => {
        localStorage.setItem(POLICY_PROFILE_SIM_RESULT_STORAGE_KEY, JSON.stringify(store));
    }, []);

    const ensurePolicySimulationResultStructure = useCallback((policyDiagramId, profileIds = []) => {
        if (!policyDiagramId) {
            return {};
        }

        const store = readPolicySimulationResultStore();
        let hasChanged = false;

        if (!store[policyDiagramId] || typeof store[policyDiagramId] !== 'object' || Array.isArray(store[policyDiagramId])) {
            store[policyDiagramId] = {};
            hasChanged = true;
        }

        const diagramStore = store[policyDiagramId];
        const uniqueProfileIds = Array.from(new Set((profileIds || []).filter(Boolean).map((id) => String(id))));

        uniqueProfileIds.forEach((profileId) => {
            if (!diagramStore[profileId] || typeof diagramStore[profileId] !== 'object' || Array.isArray(diagramStore[profileId])) {
                diagramStore[profileId] = {
                    result: {},
                    reason: {},
                };
                hasChanged = true;
                return;
            }

            if (!diagramStore[profileId].result || typeof diagramStore[profileId].result !== 'object' || Array.isArray(diagramStore[profileId].result)) {
                diagramStore[profileId].result = {};
                hasChanged = true;
            }

            if (!diagramStore[profileId].reason || typeof diagramStore[profileId].reason !== 'object' || Array.isArray(diagramStore[profileId].reason)) {
                diagramStore[profileId].reason = {};
                hasChanged = true;
            }
        });

        if (hasChanged) {
            writePolicySimulationResultStore(store);
        }

        return store;
    }, [readPolicySimulationResultStore, writePolicySimulationResultStore]);

    const getProfileResultById = useCallback((store, policyDiagramId, profileId) => {
        if (!policyDiagramId || !profileId) {
            return {
                result: {},
                reason: {},
            };
        }

        const profileStore = store?.[policyDiagramId]?.[profileId];
        const result = profileStore?.result && typeof profileStore.result === 'object' && !Array.isArray(profileStore.result)
            ? profileStore.result
            : {};
        const reason = profileStore?.reason && typeof profileStore.reason === 'object' && !Array.isArray(profileStore.reason)
            ? profileStore.reason
            : {};

        return {
            result,
            reason,
        };
    }, []);

    const applyRowResultByProfile = useCallback((sourceRows = [], profileResultStore = {}) => {
        const resultMap = profileResultStore?.result || {};
        const reasonMap = profileResultStore?.reason || {};

        return sourceRows.map((row) => {
            const rowId = String(row?.id || '');
            return {
                ...row,
                result: rowId ? (resultMap[rowId] || '') : '',
                reason: rowId ? normalizeReasonList(reasonMap[rowId]) : [],
            };
        });
    }, []);

    const collectRowResultByProfile = useCallback((sourceRows = []) => {
        const result = {};
        const reason = {};

        sourceRows.forEach((row) => {
            const rowId = String(row?.id || '');
            if (!rowId) {
                return;
            }

            result[rowId] = String(row?.result || '');
            reason[rowId] = normalizeReasonList(row?.reason);
        });

        return { result, reason };
    }, []);

    const applyStoredResultsToTables = useCallback((profileId) => {
        const selectedId = String(profileId || '');
        if (!selectedId) {
            return;
        }

        const policyDiagramId = getCurrentPolicyDiagramId();
        if (!policyDiagramId) {
            return;
        }

        const profileIds = (selectedProfiles || []).map((option) => getProfileOptionId(option)).filter(Boolean);
        const store = ensurePolicySimulationResultStructure(policyDiagramId, profileIds);
        const profileResultStore = getProfileResultById(store, policyDiagramId, selectedId);

        setApplicationRows((prevRows) => applyRowResultByProfile(prevRows, profileResultStore));
        setRestfulApiRows((prevRows) => applyRowResultByProfile(prevRows, profileResultStore));
        setDataRows((prevRows) => applyRowResultByProfile(prevRows, profileResultStore));
    }, [
        applyRowResultByProfile,
        ensurePolicySimulationResultStructure,
        getCurrentPolicyDiagramId,
        getProfileResultById,
        selectedProfiles,
    ]);

    const runSimulatePolicyTest = async (profile) => {
        // console.log("profile:", profile)

        //Case 1: Test WEB DDC-Care-Dashboard ไม่มีเงื่อนไข
        const policyRequest1 = policySimulator.createPolicyRequest(
            "d45a9c3c-2174-455b-9ab0-7e993af14b5b",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
            },
            "open",
            {}, // ไม่มี environment, public_ip
        )

        //Case 2: Test WEB DDC-Care-Dashboard มีการกำหนดเวลา (ยังตรวจสอบไม่ถูกต้อง)
        const policyRequest2 = policySimulator.createPolicyRequest(
            "d45a9c3c-2174-455b-9ab0-7e993af14b5b",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
            },
            "OPEN",
            {
                // timestamp: '2026-06-12 16:30:00'
            },
        )

        //Case 3: Test WEB DDC-Care-Dashboard มีการกำหนด IP address static
        const policyRequest3 = policySimulator.createPolicyRequest(
            "d1f7cd23-343c-467c-92f9-bccf2f9b0512",
            {
                name: "trb-watch-local",
                type: "WEB",
            },
            "OPEN",
            {
                // ip_address: "192.168.0.1", // IPv4 Static
                // ip_address: "192.168.0.*", // IPv4 Wildcard
                // ip_address: "192.168.0.0/24" // IPv4 CIDR/Subnet
                // ip_address: "192.168.0.1-192.168.0.9" // IPv4 Range
            },
            // "192.168.3.9"
            // "190.160.0.16"
        )

        //Case 4: Test WEB DDC-Care-Dashboard มีการกำหนดเวลา และ IP address ด้วย
        const policyRequest4 = policySimulator.createPolicyRequest(
            "e021af93-0b02-4e2e-b542-64137f341206",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
            },
            "open",
            {
                timestamp: '2026-06-11 07:59:00',
            },
            "192.168.0.1"
        )

        //Case 5: Test API ddccare-api-workshop ไม่มีเงื่อนไข
        const policyRequest5 = policySimulator.createPolicyRequest(
            "1c9d9edb-cf0d-41d1-bf6a-df73c273fe32",
            {
                name: "ddccare-api-workshop",
                type: "API",
                param: {
                    path: "/get-user",
                }
            },
            "GET",
            {}, // ไม่มี environment, public_ip
        )

        //Case 6: Test API ddccare-api-workshop มีการกำหนด Web ที่สามารถเข้าถึง
        const policyRequest6 = policySimulator.createPolicyRequest(
            "1c9d9edb-cf0d-41d1-bf6a-df73c273fe32",
            {
                name: "ddccare-api-workshop",
                type: "API",
                param: {
                    path: "/get-user",
                }
            },
            "GET",
            {
                name: "DDC-Care-Dashboard",
                type: "WEB",
                // timestamp: '2026-06-13 09:10:00',
            },
            "193.100.2.1"
        )

        //Case 6.1: Test API ddccare-api-workshop มีการกำหนด Web ที่สามารถเข้าถึง
        const policyRequest6_1 = policySimulator.createPolicyRequest(
            "1c9d9edb-cf0d-41d1-bf6a-df73c273fe32",
            {
                name: "ddccare-api-workshop",
                type: "API",
                param: {
                    path: "/get-user",
                }
            },
            "GET",
            {
                name: "trb-watch-local",
                type: "WEB",
                timestamp: '2026-06-13 09:10:00',
            },
            // "192.168.0.12"
        )

        //Case 7: Test DATA drg-1-1 ไม่มีเงื่อนไข
        const policyRequest7 = policySimulator.createPolicyRequest(
            "34c54747-e55e-4815-8eb0-82d5f74aebb0",
            {
                name: "drg-1-1",
                type: "DATA",
            },
            "SELECT",
            {},
        )

        const decision = await policySimulator.simulate(policyRequest2, profile?.attributes);
        // console.log(decision)
    }

    const runSimulatePolicy = async (profile) => {
        // console.log('Rows', rows)
        const policyFile = JSON.parse(localStorage.getItem('policy-file'));
        if (!policyFile) return;
        const rules = await getRuleByPolicyDiagram(policyFile);
        const mappingRules = rules.reduce((acc, item) => {
            acc[item.resource.name] = {
                type: item.resource.type,
                rule_id: item.rule_id
            };
            return acc;
        }, {});
        // console.log("mappingRules", mappingRules)
        const rowRequest = await Promise.all(
            rows.map(async (row) => {
                if (row.kind !== 'group' && row.checked !== true) {
                    if (row.result) {
                        return {
                            ...row,
                            result: "",
                        };
                    }
                    return row;
                }

                if (row.kind !== 'group' && row.checked === true) {
                    let rule_id = "";

                    if (
                        mappingRules[row.resource] &&
                        (row?.type?.toLowerCase() === mappingRules[row.resource].type.toLowerCase())
                    ) {
                        rule_id = mappingRules[row.resource].rule_id;
                    }

                    // console.log("rule_id", rule_id)

                    let policyRequest;

                    if (row.type === 'WEB') {
                        policyRequest = policySimulator.createPolicyRequest(
                            rule_id,
                            {
                                name: row.resource,
                                type: row.type,
                            },
                            row.action || undefined,
                            {
                                timestamp: row.date_time || undefined,
                            },
                            row.ip_address || undefined
                        );
                    } else if (row.type === 'API') {
                        policyRequest = policySimulator.createPolicyRequest(
                            rule_id,
                            {
                                name: row.resource,
                                type: row.type,
                                param: { path: row.param },
                            },
                            row.action || undefined,
                            {
                                name: row.environment,
                                type: row.environment_type,
                                timestamp: row.date_time || undefined,
                            },
                            row.ip_address || undefined
                        );
                    } else if (row.type === 'DATA') {
                        policyRequest = policySimulator.createPolicyRequest(
                            rule_id,
                            {
                                name: row.resource,
                                type: row.type,
                            },
                            row.action || undefined,
                            {
                                name: row.environment,
                                type: row.environment_type,
                                timestamp: row.date_time || undefined,
                            },
                            row.ip_address || undefined
                        );
                    }

                    if (policyRequest) {
                        const result = await policySimulator.simulate(
                            policyRequest,
                            profile?.attributes
                        );

                        // console.log('decision', result)

                        return {
                            ...row,
                            result: result.decision,
                        };
                    }
                }

                return row;
            })
        );
        setRows(rowRequest);
    };

    const handleRunSimulate = async () => {
        setIsLoadingTable(true);

        if (!policySimulator) {
            setIsLoadingTable(false);
            return;
        }

        const simulateRowsForProfile = async (sourceRows = [], profileAttributes = {}, requestBuilder) => {
            // console.log('simulateRowsForProfile', sourceRows, profileAttributes, requestBuilder);
            return Promise.all(
                sourceRows.map(async (row) => {
                    if (row.checked && !row.is_base_row) {
                        const policyRequest = requestBuilder(row);
                        if (policyRequest) {
                            const result = await policySimulator.simulate(policyRequest, profileAttributes);

                            // console.log('Simulate result for row', 'policyRequest:', policyRequest, 'Result:', result);

                            return {
                                ...row,
                                result: result?.decision || '',
                                reason: normalizeReasonList(result?.reasons),
                            };
                        }
                    }

                    return {
                        ...row,
                        result: '',
                        reason: [],
                    };
                })
            );
        };

        const buildApplicationPolicyRequest = (row) => policySimulator.createPolicyRequest(
            row.rule_id,
            {
                name: row.rule_resource_name || row.resource_name,
                type: row.resource_type,
            },
            'OPEN',
            {
                timestamp: row.environment_date_time === '-' ? undefined : row.environment_date_time,
            },
            row.environment_ip_address === '-' ? undefined : row.environment_ip_address
        );

        const buildRestfulApiPolicyRequest = (row) => policySimulator.createPolicyRequest(
            row.rule_id,
            {
                name: row.rule_resource_name || row.resource_name,
                type: row.resource_type,
                param: { path: row.path },
            },
            row.action || undefined,
            {
                name: row.environment_resource_name || undefined,
                type: row.environment_resource_type || undefined,
                timestamp: row.environment_date_time === '-' ? undefined : row.environment_date_time,
            },
            row.environment_ip_address === '-' ? undefined : row.environment_ip_address
        );

        const buildDataPolicyRequest = (row) => policySimulator.createPolicyRequest(
            row.rule_id,
            {
                name: row.rule_resource_name || row.resource_name,
                type: row.resource_type,
            },
            row.action || undefined,
            {
                name: row.environment_resource_name || undefined,
                type: row.environment_resource_type || undefined,
                timestamp: row.environment_date_time === '-' ? undefined : row.environment_date_time,
            },
            row.environment_ip_address === '-' ? undefined : row.environment_ip_address
        );

        const hasSelectedProfileId = Boolean(selectedProfileSimulate && selectedProfileId);

        if (!hasSelectedProfileId) {
            const fallbackProfileId = getProfileOptionId(selectedProfile);
            const fallbackProfile = simulatorProfiles.find((profile) => getProfileOptionId(profile) === fallbackProfileId) || selectedProfile;
            const fallbackProfileAttributes = fallbackProfile?.attributes || {};

            const simulatedApplicationRows = await simulateRowsForProfile(
                applicationRows,
                fallbackProfileAttributes,
                buildApplicationPolicyRequest
            );

            const simulatedRestfulApiRows = await simulateRowsForProfile(
                restfulApiRows,
                fallbackProfileAttributes,
                buildRestfulApiPolicyRequest
            );

            const simulatedDataRows = await simulateRowsForProfile(
                dataRows,
                fallbackProfileAttributes,
                buildDataPolicyRequest
            );

            setApplicationRows(simulatedApplicationRows);
            setRestfulApiRows(simulatedRestfulApiRows);
            setDataRows(simulatedDataRows);
            setIsLoadingTable(false);
            return;
        }

        const policyDiagramId = getCurrentPolicyDiagramId();
        if (!policyDiagramId) {
            return;
        }

        const profilesToSimulate = (selectedProfiles || [])
            .map((option) => {
                const profileId = getProfileOptionId(option);
                if (!profileId) {
                    return null;
                }

                const latestProfile = selectedProfiles.find((profile) => getProfileOptionId(profile) === profileId) || option;
                return {
                    id: profileId,
                    profile: latestProfile,
                };
            })
            .filter(Boolean);

        if (profilesToSimulate.length === 0) {
            setIsLoadingTable(false);
            return;
        }

        const profileIds = profilesToSimulate.map((item) => item.id);
        const store = ensurePolicySimulationResultStructure(policyDiagramId, profileIds);

        const simulatedRowsByProfileId = {};

        for (const profileItem of profilesToSimulate) {
            const profileId = profileItem.id;
            const profileAttributes = profileItem?.profile?.attributes || {};

            const simulatedApplicationRows = await simulateRowsForProfile(
                applicationRows,
                profileAttributes,
                buildApplicationPolicyRequest
            );

            const simulatedRestfulApiRows = await simulateRowsForProfile(
                restfulApiRows,
                profileAttributes,
                buildRestfulApiPolicyRequest
            );

            const simulatedDataRows = await simulateRowsForProfile(
                dataRows,
                profileAttributes,
                buildDataPolicyRequest
            );

            simulatedRowsByProfileId[profileId] = {
                applicationRows: simulatedApplicationRows,
                restfulApiRows: simulatedRestfulApiRows,
                dataRows: simulatedDataRows,
            };

            const mergedRows = [
                ...simulatedApplicationRows,
                ...simulatedRestfulApiRows,
                ...simulatedDataRows,
            ];

            store[policyDiagramId][profileId] = collectRowResultByProfile(mergedRows);
        }

        writePolicySimulationResultStore(store);

        const profileIdForDisplay = selectedProfileId || profilesToSimulate[0]?.id || '';
        const displayRows = simulatedRowsByProfileId[profileIdForDisplay];

        if (displayRows) {
            setApplicationRows(displayRows.applicationRows);
            setRestfulApiRows(displayRows.restfulApiRows);
            setDataRows(displayRows.dataRows);
            setIsLoadingTable(false);
            return;
        }

        applyStoredResultsToTables(profileIdForDisplay);
        setIsLoadingTable(false);
    };

    const onProfileChanged = (value) => {
        setIsProfileChanged(value)
    };

    const onSelectedProfile = async (profile) => {
        return;
    }

    const onSaveProfile = async (profile) => {
        try {
            const profiles = simulatorProfiles.map(pf => {
                if (pf.id === profile.id) {
                    const updatedAttr = { ...pf.attributes, ...profile.attributes };
                    const updatedProfile = {
                        ...pf,
                        name: profile.name,
                        attributes: updatedAttr,
                        updated_at: profile.updated_at
                    }
                    onSelectedProfile(updatedProfile);
                    return updatedProfile
                }
                return pf;
            });
            // console.log("profiles:", profiles)
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    profiles: profiles
                })
            })
            if (response.ok) {
                // console.log("saved profile.", response)
                dispatch(setSimulatorProfiles(sortedProfilesAttributes(profiles)));
            } else {
                throw response;
            }
        } catch (error) {
            console.error("Error save profile: ", error);
            dispatch(setSnackbarMessage({ message: 'Failed to saved profile.', status: 'error' }));
        }
    };

    const createCloneProfileName = (originalName) => {
        let counter = 1;
        let newName = `${originalName}-copy${counter}`;

        while (simulatorProfiles.some(profile => profile.name === newName)) {
            counter++;
            newName = `${originalName}-copy${counter}`;
        }

        return newName;
    };

    const onCloneProfile = async (profile) => {
        const attributes = simulatorProfiles.find(pf => pf.id === profile.id).attributes || {};

        const newProfile = {
            id: uuidv4(),
            name: createCloneProfileName(profile.name),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            attributes: attributes,
        };

        await handleAddProfile(newProfile);
    };

    const handleAddProfile = async (newProfile) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(newProfile)
            });

            if (response.ok) {
                const result = await response.json();
                const updatedProfiles = [...simulatorProfiles, newProfile];
                dispatch(setSimulatorProfiles(sortedProfilesAttributes(updatedProfiles) || []));
                setSelectedEditProfile(newProfile);
                return;
            } else {
                throw new Error("Failed to add simulator profile");
            }
        } catch (error) {
            console.error(error.message);
            dispatch(setSnackbarMessage({
                open: true,
                message: error.message,
                status: "error",
            }));
        }
    };

    const createNewProfileName = () => {
        const hasUnnamed = simulatorProfiles.some(profile => profile.name === "unname");
        if (!hasUnnamed) {
            return "unname";
        }

        let counter = 1;
        let newName = `unname-${counter}`;

        while (simulatorProfiles.some(profile => profile.name === newName)) {
            counter++;
            newName = `unname-${counter}`;
        }

        return newName;
    };

    const onAddNewProfile = async () => {
        let usedAttributes = {};
        for (const attr of subjectSchema) {
            const is_type = dataTypes.find(dt => dt.similar_type.includes(attr.attributeType));
            if (is_type.type === 'boolean') {
                usedAttributes[attr.name] = false;
            } else if (is_type.type === 'array') {
                usedAttributes[attr.name] = [];
            } else {
                usedAttributes[attr.name] = null;
            }
        }

        const newProfile = {
            id: uuidv4(),
            name: createNewProfileName(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            attributes: usedAttributes,
        };

        await handleAddProfile(newProfile);
    };

    const handleOpenProfileManagement = () => {
        setSelectedEditProfile(null);
        setOpenManagement(true);
    };

    const getUsableIp = (ip_address) => {
        if (!ip_address) return null;

        // 1. เอาค่าแรกสุด
        const firstIp = ip_address.split(",")[0].trim();

        // 2. ตรวจประเภท

        // RANGE (เช่น 1.1.1.1-1.1.1.10)
        if (firstIp.includes("-")) {
            const [start] = firstIp.split("-");
            return start.trim();
        }

        // CIDR (เช่น 192.168.1.0/24)
        if (firstIp.includes("/")) {
            const [network, prefix] = firstIp.split("/");
            const baseIp = network.split(".").map(Number);

            // แปลงเป็น usable IP (เอา +1 จาก network)
            if (prefix === "32") return network; // usable อยู่แล้ว

            baseIp[3] += 1; // เพิ่ม host ตัวแรก
            return baseIp.join(".");
        }

        // WILDCARD (เช่น 192.168.1.*)
        if (firstIp.includes("*")) {
            const parts = firstIp.split(".");

            return parts
                .map((p, i) => {
                    if (p === "*") {
                        return i === 3 ? "1" : "0"; // ปกติให้ host เป็น 1
                    }
                    return p;
                })
                .join(".");
        }

        return firstIp;
    }

    const getDefaultDateTime = (timeCondition) => {
        const now = dayjs();

        if (!timeCondition || typeof timeCondition !== 'object') {
            return now.format("YYYY-MM-DD HH:mm:ss");
        }

        const getValidSlotsByDay = (dayKey) => {
            const slots = timeCondition?.[dayKey];
            if (!Array.isArray(slots)) return [];
            return slots.filter((slot) => String(slot?.start || '').trim());
        };

        const buildDateTimeFromStart = (baseDate, start) => {
            const timeParts = String(start).split(':').map(Number);
            const hour = Number.isFinite(timeParts[0]) ? timeParts[0] : 0;
            const minute = Number.isFinite(timeParts[1]) ? timeParts[1] : 0;
            const second = Number.isFinite(timeParts[2]) ? timeParts[2] : 0;

            return baseDate
                .hour(hour)
                .minute(minute)
                .second(second)
                .millisecond(0)
                .format("YYYY-MM-DD HH:mm:ss");
        };

        const todayKey = now.format('dddd').toLowerCase();
        const todaySlots = getValidSlotsByDay(todayKey);
        if (todaySlots.length > 0) {
            return buildDateTimeFromStart(now, todaySlots[0].start);
        }

        for (let i = 1; i <= 14; i += 1) {
            const previousDay = now.subtract(i, 'day');
            const previousKey = previousDay.format('dddd').toLowerCase();
            const previousSlots = getValidSlotsByDay(previousKey);
            if (previousSlots.length > 0) {
                return buildDateTimeFromStart(previousDay, previousSlots[0].start);
            }
        }

        return now.format("YYYY-MM-DD HH:mm:ss");
    }

    const initialRequest = (rules) => {
        // Rule Web
        let webRequest = [
            {
                id: uuidv4(),
                kind: "group",
                checked: false,
                resource: "Applications"
            }
        ];
        for (const r of rules) {
            if (r?.resource?.type === 'WEB' || r?.resource?.type === 'NATIVE') {
                let request = {
                    id: uuidv4(),
                    kind: "item",
                    checked: false,
                    resource: r.resource.name,
                    type: r?.resource?.type,
                    action: r.action[0],
                    result: "",
                    is_default_item: true,
                };
                if (r?.environment?.ip_address) {
                    request['ip_address'] = getUsableIp(r?.environment?.ip_address);
                }
                if (Object.keys(r?.environment?.time).length > 0) {
                    request['date_time'] = getDefaultDateTime(r?.environment?.time);
                }
                webRequest.push(request);
            }
        }

        //Rule API
        let apiRequest = [
            {
                id: uuidv4(),
                kind: "group",
                checked: false,
                resource: "RESTful-API"
            }
        ];
        for (const r of rules) {
            if (r?.resource?.type === 'API') {
                if (r?.environment?.resources.length === 0) {
                    let request = {
                        id: uuidv4(),
                        kind: "item",
                        checked: false,
                        resource: r.resource.name,
                        type: r?.resource?.type,
                        action: r.action[0],
                        param: r.resource.param.path,
                        environment: "",
                        environment_type: "",
                        result: "",
                        is_default_item: true,
                    };
                    if (r?.environment?.ip_address) {
                        request['ip_address'] = getUsableIp(r?.environment?.ip_address)
                    }
                    if (Object.keys(r?.environment?.time).length > 0) {
                        request['date_time'] = getDefaultDateTime(r?.environment?.time);
                    }
                    apiRequest.push(request);
                } else {
                    const resources = r.environment.resources;
                    for (let i = 0; i < resources.length; i++) {
                        let request = {
                            id: uuidv4(),
                            kind: "item",
                            checked: false,
                            resource: r.resource.name,
                            type: r?.resource?.type,
                            action: r.action[0],
                            param: r.resource.param.path,
                            environment: resources[i].name,
                            environment_type: resources[i].type,
                            result: "",
                            is_default_item: true,
                        };
                        if (r?.environment?.ip_address) {
                            request['ip_address'] = getUsableIp(r?.environment?.ip_address)
                        }
                        if (Object.keys(r?.environment?.time).length > 0) {
                            request['date_time'] = getDefaultDateTime(r?.environment?.time);
                        }
                        apiRequest.push(request);
                    }
                }
            }
        }

        //Rule Data
        let dataRequest = [
            {
                id: uuidv4(),
                kind: "group",
                checked: false,
                resource: "Data"
            }
        ];
        for (const r of rules) {
            if (r?.resource?.type === 'DATA') {
                if (r?.environment?.resources.length === 0) {
                    let request = {
                        id: uuidv4(),
                        kind: "item",
                        checked: false,
                        resource: r.resource.name,
                        type: r?.resource?.type,
                        action: r.action[0],
                        // param: r.resource.param,
                        environment: "",
                        environment_type: "",
                        result: "",
                        is_default_item: true,
                    };
                    if (r?.environment?.ip_address) {
                        request['ip_address'] = getUsableIp(r?.environment?.ip_address)
                    }
                    if (Object.keys(r?.environment?.time).length > 0) {
                        request['date_time'] = getDefaultDateTime(r?.environment?.time);
                    }
                    dataRequest.push(request);
                } else {
                    const resources = r.environment.resources;
                    for (let i = 0; i < resources.length; i++) {
                        let request = {
                            id: uuidv4(),
                            kind: "item",
                            checked: false,
                            resource: r.resource.name,
                            type: r?.resource?.type,
                            action: r.action[0],
                            // param: r.resource.param,
                            environment: resources[i].name,
                            environment_type: resources[i].type,
                            result: "",
                            is_default_item: true,
                        };
                        if (r?.environment?.ip_address) {
                            request['ip_address'] = getUsableIp(r?.environment?.ip_address)
                        }
                        if (Object.keys(r?.environment?.time).length > 0) {
                            request['date_time'] = getDefaultDateTime(r?.environment?.time);
                        }
                        dataRequest.push(request);
                    }
                }
            }
        }

        //Merge initial request
        const result = [...webRequest, ...apiRequest, ...dataRequest]
        return result;
    };

    const buildRulesSnapshot = (rules = []) => {
        if (!Array.isArray(rules)) return [];

        const snapshot = rules.map((rule) => {
            const resourceType = String(rule?.resource?.type || '').toUpperCase();
            const envResources = Array.isArray(rule?.environment?.resources)
                ? rule.environment.resources.map((res) => ({
                    name: String(res?.name || ''),
                    type: String(res?.type || ''),
                }))
                : [];

            envResources.sort((a, b) => `${a.name}:${a.type}`.localeCompare(`${b.name}:${b.type}`));

            return {
                resource_name: String(rule?.resource?.name || ''),
                resource_type: resourceType,
                action: Array.isArray(rule?.action) ? [...rule.action] : [],
                param_path: String(rule?.resource?.param?.path || ''),
                environment_ip_address: String(rule?.environment?.ip_address || ''),
                environment_time: rule?.environment?.time && typeof rule.environment.time === 'object'
                    ? JSON.stringify(rule.environment.time)
                    : '',
                environment_resources: envResources,
            };
        });

        snapshot.sort((a, b) => `${a.resource_name}:${a.resource_type}:${a.param_path}`.localeCompare(`${b.resource_name}:${b.resource_type}:${b.param_path}`));
        return snapshot;
    };

    const isRulesSnapshotEqual = (left = [], right = []) => {
        return JSON.stringify(left || []) === JSON.stringify(right || []);
    };

    const hasMatchingRule = (row, rules = []) => {
        if (!row || row.kind !== 'item') return false;

        return rules.some((rule) =>
            String(rule?.resource?.name || '') === String(row?.resource || '') &&
            String(rule?.resource?.type || '').toUpperCase() === String(row?.type || '').toUpperCase()
        );
    };

    const mergeRowsWhenRulesChanged = (existingRows = [], rules = []) => {
        const freshRows = initialRequest(rules);
        const safeExistingRows = Array.isArray(existingRows) ? existingRows : [];

        const customRowsByType = { WEB: [], API: [], DATA: [] };
        safeExistingRows.forEach((row) => {
            if (row?.kind !== 'item' || row?.is_default_item) return;
            if (!hasMatchingRule(row, rules)) return;

            const rowType = String(row?.type || '').toUpperCase();
            if (rowType === 'API') {
                customRowsByType.API.push(row);
            } else if (rowType === 'DATA') {
                customRowsByType.DATA.push(row);
            } else {
                customRowsByType.WEB.push(row);
            }
        });

        const groupedFresh = {
            WEB: freshRows.filter((row) => row.kind === 'item' && (row.type === 'WEB' || row.type === 'NATIVE')),
            API: freshRows.filter((row) => row.kind === 'item' && row.type === 'API'),
            DATA: freshRows.filter((row) => row.kind === 'item' && row.type === 'DATA'),
        };

        return [
            { id: uuidv4(), kind: 'group', checked: false, resource: 'Applications' },
            ...groupedFresh.WEB,
            ...customRowsByType.WEB,
            { id: uuidv4(), kind: 'group', checked: false, resource: 'RESTful-API' },
            ...groupedFresh.API,
            ...customRowsByType.API,
            { id: uuidv4(), kind: 'group', checked: false, resource: 'Data' },
            ...groupedFresh.DATA,
            ...customRowsByType.DATA,
        ];
    };

    // const handleCheckInitial = async () => {
    //     const policyRequest = JSON.parse(localStorage.getItem('policy-simulator'));
    //     const policyFile = JSON.parse(localStorage.getItem('policy-file'));
    //     const policyDiagramId = policyFile?.policy_diagram_id;
    //     const profileId = selectedProfileId;

    //     if (!policyFile || !policyDiagramId || !profileId) return;

    //     const currentRequest =
    //         policyRequest && typeof policyRequest === 'object' ? { ...policyRequest } : {};
    //     const rawDiagramStore = currentRequest[policyDiagramId];

    //     const rules = await getRuleByPolicyDiagram(policyFile);
    //     const latestRulesSnapshot = buildRulesSnapshot(rules);

    //     let diagramStore = {};
    //     if (rawDiagramStore && !Array.isArray(rawDiagramStore) && typeof rawDiagramStore === 'object') {
    //         diagramStore = { ...rawDiagramStore };
    //     }

    //     // Backward compatibility: migrate old structure { [diagramId]: rows[] } to profile-level storage.
    //     if (Array.isArray(rawDiagramStore)) {
    //         diagramStore[profileId] = {
    //             rows: rawDiagramStore,
    //             rules_snapshot: latestRulesSnapshot,
    //         };
    //     }

    //     const resetRowResults = (sourceRows = []) => {
    //         if (!Array.isArray(sourceRows)) return [];
    //         return sourceRows.map((row) => {
    //             if (row?.kind !== 'item') return row;
    //             return {
    //                 ...row,
    //                 result: "",
    //             };
    //         });
    //     };

    //     const profileStoreRaw = diagramStore[profileId];
    //     let profileStore = null;

    //     if (Array.isArray(profileStoreRaw)) {
    //         profileStore = {
    //             rows: profileStoreRaw,
    //             rules_snapshot: [],
    //         };
    //     } else if (profileStoreRaw && typeof profileStoreRaw === 'object') {
    //         profileStore = {
    //             rows: Array.isArray(profileStoreRaw.rows) ? profileStoreRaw.rows : [],
    //             rules_snapshot: Array.isArray(profileStoreRaw.rules_snapshot) ? profileStoreRaw.rules_snapshot : [],
    //         };
    //     }

    //     if (!profileStore) {
    //         const initialRows = resetRowResults(initialRequest(rules));
    //         diagramStore[profileId] = {
    //             rows: initialRows,
    //             rules_snapshot: latestRulesSnapshot,
    //         };
    //         currentRequest[policyDiagramId] = diagramStore;

    //         setRows(initialRows);
    //         localStorage.setItem('policy-simulator', JSON.stringify(currentRequest));
    //         return;
    //     }

    //     const rulesChanged = !isRulesSnapshotEqual(profileStore.rules_snapshot, latestRulesSnapshot);

    //     if (!rulesChanged) {
    //         const rowsWithResetResult = resetRowResults(profileStore.rows);
    //         setRows(rowsWithResetResult);
    //         diagramStore[profileId] = {
    //             rows: rowsWithResetResult,
    //             rules_snapshot: latestRulesSnapshot,
    //         };
    //         currentRequest[policyDiagramId] = diagramStore;
    //         localStorage.setItem('policy-simulator', JSON.stringify(currentRequest));
    //         return;
    //     }

    //     const mergedRows = resetRowResults(mergeRowsWhenRulesChanged(profileStore.rows, rules));
    //     diagramStore[profileId] = {
    //         rows: mergedRows,
    //         rules_snapshot: latestRulesSnapshot,
    //     };
    //     currentRequest[policyDiagramId] = diagramStore;

    //     setRows(mergedRows);
    //     localStorage.setItem('policy-simulator', JSON.stringify(currentRequest));

    // };

    const handleChangeProfile = (selectedProfileId) => {
        const profileId = String(selectedProfileId || '');

        if (!profileId) {
            dispatch(setSelectedProfileSimulate(null));
            return;
        }

        dispatch(setSelectedProfileSimulate({ id: profileId }));
        applyStoredResultsToTables(profileId);
    };

    const handleCheckInitialRequest = useCallback(async () => {
        const policyFile = JSON.parse(localStorage.getItem('policy-file') || '{}');
        const policyDiagramId = policyFile?.policy_diagram_id;
        if (!policyFile || !policyDiagramId) {
            setApplicationRows([]);
            setRestfulApiRows([]);
            setDataRows([]);
            return;
        }

        const readBuilderProfilesByKey = (dataKey) => {
            const rawStore = localStorage.getItem(POLICY_PROFILE_BUILDER_STORAGE_KEY);
            if (!rawStore) return [];

            try {
                const parsedStore = JSON.parse(rawStore);
                const sections = Array.isArray(parsedStore?.[policyDiagramId]) ? parsedStore[policyDiagramId] : [];
                const targetSection = sections.find((section) => section?.dataKey === dataKey);
                const profiles = Array.isArray(targetSection?.profiles) ? targetSection.profiles : [];

                return profiles
                    .filter((profile) => profile && typeof profile === 'object')
                    .map((profile) => ({
                        id: profile?.id || profile?.profile_id || uuidv4(),
                        profile_name: String(profile?.profile_name || '').trim(),
                        environment_resource_name: String(profile?.environment_resource_name || '').trim(),
                        path: String(profile?.path || '').trim(),
                        ip_address: String(profile?.ip_address || '').trim(),
                        date_time: formatDateTimeDisplay(profile?.date_time),
                        action: Array.isArray(profile?.action)
                            ? profile.action.map((action) => String(action || '').trim()).filter(Boolean)
                            : [],
                    }))
                    .filter((profile) => profile.profile_name.length > 0);
            } catch (error) {
                return [];
            }
        };

        const normalizeEnvironmentDateTime = (dateTime) => {
            if (!dateTime || typeof dateTime !== 'object' || Array.isArray(dateTime)) {
                return '-';
            }

            const dayOrder = [
                { key: 'monday', label: 'Mon.' },
                { key: 'tuesday', label: 'Tue.' },
                { key: 'wednesday', label: 'Wed.' },
                { key: 'thursday', label: 'Thu.' },
                { key: 'friday', label: 'Fri.' },
                { key: 'saturday', label: 'Sat.' },
                { key: 'sunday', label: 'Sun.' },
            ];

            const validDays = dayOrder
                .map((day, index) => {
                    const slots = dateTime?.[day.key];
                    if (!Array.isArray(slots) || slots.length === 0) {
                        return null;
                    }

                    const normalizedSlots = slots
                        .map((slot) => {
                            const start = String(slot?.start || '').trim();
                            const end = String(slot?.end || '').trim();
                            if (!start || !end) {
                                return null;
                            }
                            return `${start}-${end}`;
                        })
                        .filter(Boolean);

                    if (normalizedSlots.length === 0) {
                        return null;
                    }

                    return {
                        index,
                        label: day.label,
                        slotsText: normalizedSlots.join(','),
                    };
                })
                .filter(Boolean);

            if (validDays.length === 0) {
                return '-';
            }

            const groupedDays = [];
            validDays.forEach((day) => {
                const latestGroup = groupedDays[groupedDays.length - 1];
                const canMerge = (
                    latestGroup
                    && latestGroup.slotsText === day.slotsText
                    && day.index === latestGroup.endIndex + 1
                );

                if (canMerge) {
                    latestGroup.endIndex = day.index;
                    latestGroup.endLabel = day.label;
                    return;
                }

                groupedDays.push({
                    startIndex: day.index,
                    endIndex: day.index,
                    startLabel: day.label,
                    endLabel: day.label,
                    slotsText: day.slotsText,
                });
            });

            return groupedDays
                .map((group) => {
                    const dayLabel = group.startIndex === group.endIndex
                        ? group.startLabel
                        : `${group.startLabel} - ${group.endLabel}`;

                    return `${dayLabel} (${group.slotsText})`;
                })
                .join(', ');
        };

        const rules = await getRuleByPolicyDiagram(policyFile);
        const safeRules = Array.isArray(rules) ? rules : [];

        const applicationProfiles = readBuilderProfilesByKey('application');
        const restfulApiProfiles = readBuilderProfilesByKey('restful_api');
        const dataProfiles = readBuilderProfilesByKey('data');

        const initialApplicationRows = [];
        const initialRestfulApiRows = [];
        const initialDataRows = [];

        safeRules.forEach((rule) => {
            const resourceType = String(rule?.resource?.type || '').toUpperCase();
            const ruleResourceName = String(rule?.resource?.name || '').trim();

            if (!ruleResourceName) {
                return;
            }

            if (resourceType === 'WEB' || resourceType === 'NATIVE') {
                const baseRowId = uuidv4();
                initialApplicationRows.push({
                    id: baseRowId,
                    base_row_id: baseRowId,
                    is_base_row: true,
                    rule_id: rule.rule_id,
                    rule_resource_name: ruleResourceName,
                    resource_name: ruleResourceName,
                    profile_name: '-',
                    resource_type: rule?.resource?.type,
                    checked: false,
                    action: 'OPEN',
                    environment_ip_address: rule?.environment?.ip_address || '-',
                    environment_date_time: normalizeEnvironmentDateTime(rule?.environment?.time) || '-',
                    result: '',
                    reason: [],
                    // error_column: [],
                });
                return;
            }

            if (resourceType === 'API') {
                const environmentResources = Array.isArray(rule?.environment?.resources) && rule.environment.resources.length > 0
                    ? rule.environment.resources
                    : [{ name: '-', type: '-' }];

                const normalizedActions = Array.isArray(rule?.action)
                    ? rule.action.map((action) => String(action || '').trim()).filter(Boolean)
                    : [];

                const actionValue = normalizedActions.length === 1
                    ? normalizedActions[0]
                    : normalizedActions.length > 1
                        ? normalizedActions.join(',')
                        : '-';

                environmentResources.forEach((env) => {
                    const baseRowId = uuidv4();
                    let apiPath = '-';
                    if (
                        rule?.resource?.param &&
                        typeof rule?.resource?.param === "object" &&
                        !Array.isArray(rule?.resource?.param) &&
                        Object.keys(rule?.resource?.param).length === 0
                    ) {
                        apiPath = '/*';
                    } else {
                        apiPath = rule?.resource?.param?.path || '-';
                    }
                    initialRestfulApiRows.push({
                        id: baseRowId,
                        base_row_id: baseRowId,
                        is_base_row: true,
                        rule_id: rule.rule_id,
                        rule_resource_name: ruleResourceName,
                        resource_name: ruleResourceName,
                        profile_name: '-',
                        resource_type: rule?.resource?.type,
                        checked: false,
                        action: actionValue,
                        path: apiPath,
                        environment_resource: env?.name || '-',
                        environment_resource_name: env?.name || '-',
                        environment_resource_type: env?.type || '-',
                        environment_ip_address: '-',
                        environment_date_time: '-',
                        result: '',
                        reason: [],
                        // error_column: [],
                    });
                });
                return;
            }

            if (resourceType === 'DATA') {
                const environmentResources = Array.isArray(rule?.environment?.resources) && rule.environment.resources.length > 0
                    ? rule.environment.resources
                    : [{ name: '-', type: '-' }];

                environmentResources.forEach((env) => {
                    const baseRowId = uuidv4();
                    initialDataRows.push({
                        id: baseRowId,
                        base_row_id: baseRowId,
                        is_base_row: true,
                        rule_id: rule.rule_id,
                        rule_resource_name: ruleResourceName,
                        resource_name: ruleResourceName,
                        profile_name: '-',
                        resource_type: rule?.resource?.type,
                        checked: false,
                        action: Array.isArray(rule?.action) && rule.action.length > 0 ? rule.action[0] : '-',
                        environment_resource: env?.name || '-',
                        environment_resource_name: env?.name || '-',
                        environment_resource_type: env?.type || '-',
                        environment_ip_address: '-',
                        environment_date_time: '-',
                        result: '',
                        reason: [],
                        // error_column: [],
                    });
                });
            }
        });

        const interleavedApplicationRows = [];
        initialApplicationRows.forEach((baseRow) => {
            interleavedApplicationRows.push(baseRow);

            applicationProfiles.forEach((profile) => {
                interleavedApplicationRows.push({
                    ...baseRow,
                    id: uuidv4(),
                    base_row_id: baseRow.base_row_id || baseRow.id,
                    is_base_row: false,
                    checked: false,
                    result: '',
                    reason: [],
                    // error_column: [],
                    profile_name: profile.profile_name,
                    environment_ip_address: profile.ip_address || baseRow.environment_ip_address || '-',
                    environment_date_time: formatDateTimeDisplay(profile.date_time || baseRow.environment_date_time),
                });
            });
        });

        const interleavedRestfulApiRows = [];
        initialRestfulApiRows.forEach((baseRow) => {
            interleavedRestfulApiRows.push(baseRow);

            restfulApiProfiles.forEach((profile) => {
                const profileActions = Array.isArray(profile.action) && profile.action.length > 0
                    ? profile.action
                    : [baseRow.action];

                profileActions.forEach((action) => {
                    interleavedRestfulApiRows.push({
                        ...baseRow,
                        id: uuidv4(),
                        base_row_id: baseRow.base_row_id || baseRow.id,
                        is_base_row: false,
                        checked: false,
                        result: '',
                        reason: [],
                        // error_column: [],
                        profile_name: profile.profile_name,
                        action,
                        path: profile.path || '-',
                        environment_resource_name: profile.environment_resource_name || '-',
                        environment_ip_address: profile.ip_address || baseRow.environment_ip_address || '-',
                        environment_date_time: formatDateTimeDisplay(profile.date_time || baseRow.environment_date_time),
                    });
                });
            });
        });

        const interleavedDataRows = [];
        initialDataRows.forEach((baseRow) => {
            interleavedDataRows.push(baseRow);

            dataProfiles.forEach((profile) => {
                const profileActions = Array.isArray(profile.action) && profile.action.length > 0
                    ? profile.action
                    : [baseRow.action];

                profileActions.forEach((action) => {
                    interleavedDataRows.push({
                        ...baseRow,
                        id: uuidv4(),
                        base_row_id: baseRow.base_row_id || baseRow.id,
                        is_base_row: false,
                        checked: false,
                        result: '',
                        reason: [],
                        // error_column: [],
                        profile_name: profile.profile_name,
                        action,
                        environment_resource_name: profile.environment_resource_name || '-',
                        environment_ip_address: profile.ip_address || baseRow.environment_ip_address || '-',
                        environment_date_time: formatDateTimeDisplay(profile.date_time || baseRow.environment_date_time),
                    });
                });
            });
        });

        setApplicationRows(interleavedApplicationRows);
        setRestfulApiRows(interleavedRestfulApiRows);
        setDataRows(interleavedDataRows);
    }, []);

    useEffect(() => {
        if (openSimulatorPolicy) {
            setMemoizedAttributes(findSubjectAttrToSimulator(nodes));
            handleCheckInitialRequest();
        }
    }, [openSimulatorPolicy, nodes, handleCheckInitialRequest]);

    useEffect(() => {
        const handleProfileBuilderChanged = () => {
            if (!openSimulatorPolicy) {
                return;
            }
            handleCheckInitialRequest();
        };

        window.addEventListener(PROFILE_BUILDER_CHANGED_EVENT, handleProfileBuilderChanged);
        return () => {
            window.removeEventListener(PROFILE_BUILDER_CHANGED_EVENT, handleProfileBuilderChanged);
        };
    }, [openSimulatorPolicy, handleCheckInitialRequest]);

    useEffect(() => {
        const hasSelectedRows = [
            ...applicationRows,
            ...restfulApiRows,
            ...dataRows,
        ].some((row) => row?.checked === true && !row?.is_base_row);

        if (!hasSelectedRows) {
            setDisabledRunSimulate(true);
            return;
        }

        const hasSubjectAttributes = Boolean(memoizedAttributes && Object.keys(memoizedAttributes).length > 0);
        if (!hasSubjectAttributes) {
            setDisabledRunSimulate(false);
            return;
        }

        const hasSelectedProfiles = Array.isArray(selectedProfiles) && selectedProfiles.length > 0;
        setDisabledRunSimulate(!hasSelectedProfiles);
    }, [memoizedAttributes, selectedProfiles, applicationRows, restfulApiRows, dataRows]);

    useEffect(() => {
        if (!openSimulatorPolicy) {
            return;
        }

        const profileIds = (selectedProfiles || []).map((profile) => getProfileOptionId(profile)).filter(Boolean);
        const profileIdForDisplay = selectedProfileId || profileIds[0] || '';

        if (!profileIdForDisplay) {
            setApplicationRows((prevRows) => prevRows.map((row) => ({ ...row, result: '', reason: [] })));
            setRestfulApiRows((prevRows) => prevRows.map((row) => ({ ...row, result: '', reason: [] })));
            setDataRows((prevRows) => prevRows.map((row) => ({ ...row, result: '', reason: [] })));
            return;
        }

        applyStoredResultsToTables(profileIdForDisplay);
    }, [
        openSimulatorPolicy,
        selectedProfileId,
        selectedProfiles,
        applicationRows.length,
        restfulApiRows.length,
        dataRows.length,
        applyStoredResultsToTables,
    ]);

    return (
        <>
            <Panel direction="vertical" defaultSize={40}>
                <PanelGroup direction="horizontal" id="simulator-panel-group">
                    <Panel
                        id="policy-request-panel"
                        style={{
                            overflow: 'hidden',
                            width: 100
                        }}
                    >
                        <AppBar position="static" sx={{ height: "30px", width: "100%", boxShadow: 'none', backgroundColor: "whitesmoke", flexShrink: 0 }}>
                            <Toolbar disableGutters variant="dense" sx={{ minHeight: '30px', height: '30px', marginLeft: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                <div className='flex justify-center items-center'>
                                    <Typography fontSize={12} className='text-gray-600'>
                                        Simulation Requests
                                    </Typography>
                                    <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 0.5 }} />
                                    <div
                                        className={`flex justify-center items-center pl-1.5 rounded-full ${(disabledRunSimulate || isLoadingTable) ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 cursor-pointer'}`}
                                        onClick={() => {
                                            if (disabledRunSimulate || isLoadingTable) return;
                                            handleRunSimulate()
                                            // runSimulatePolicy()
                                        }}
                                    >
                                        {/* <Typography variant='button' fontSize={10} className='text-white' marginTop="2px">
                                            Run
                                        </Typography>
                                        <PlayCircleFilledWhiteIcon
                                            color="white"
                                            sx={{ fontSize: 18, alignItems: 'center', padding: '1px' }}
                                            onClick={() => {
                                                if (disabledRunSimulate) return;
                                                handleRunSimulate()
                                                // runSimulatePolicy()
                                            }}
                                        /> */}
                                        <Typography variant='button' fontSize={10} className='text-white' marginTop="2px">
                                            {isLoadingTable ? 'Running' : 'Run'}
                                        </Typography>
                                        {isLoadingTable ? (
                                            <CircularProgress aria-label="running…" size="14px" color='#fff' sx={{ marginX: '6px', marginY: '5px' }} />
                                        ) : (
                                            <PlayCircleFilledWhiteIcon
                                                color="white"
                                                sx={{ fontSize: 18, alignItems: 'center', padding: '1px' }}
                                            />
                                        )}
                                    </div>
                                    {(selectedProfiles && selectedProfiles.length > 0) && (
                                        <>
                                            <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 0.5 }} />
                                            <FormControl
                                                size="small"
                                                sx={{
                                                    width: 140,
                                                    height: 24,
                                                    my: '2px',
                                                    '& .MuiOutlinedInput-root': {
                                                        height: 24,
                                                        fontSize: 12,
                                                        backgroundColor: 'white',
                                                    },
                                                    '& .MuiSelect-select': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        minHeight: '24px !important',
                                                        py: 0,
                                                        pr: 3,
                                                        backgroundColor: 'white',
                                                    },
                                                }}
                                            >
                                                <Select
                                                    value={getProfileOptionId(selectedProfile)}
                                                    onChange={(e) => {
                                                        handleChangeProfile(String(e.target.value || ''));
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: '#fff',
                                                            '&:hover': {
                                                                backgroundColor: '#fff',
                                                            },
                                                            '&.Mui-focused': {
                                                                backgroundColor: '#fff',
                                                            },
                                                        },
                                                        '& .MuiSelect-select': {
                                                            backgroundColor: '#fff',
                                                        },
                                                    }}
                                                    renderValue={(selectedId) => {
                                                        const selectedOption = (selectedProfiles || []).find((option) => getProfileOptionId(option) === String(selectedId || ''));
                                                        return selectedOption?.name || '';
                                                    }}
                                                >
                                                    {selectedProfiles.map((option) => (
                                                        <MenuItem key={getProfileOptionId(option)} value={getProfileOptionId(option)} sx={{ fontSize: 12 }}>
                                                            {option.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </>
                                    )}
                                </div>
                                {/* <div></div> */}
                                <IconButton
                                    aria-label="close console"
                                    aria-controls="close-console"
                                    onClick={onClose}
                                    color="default"
                                    style={{
                                        height: '25px',
                                        width: '25px',
                                        marginRight: '4px'
                                    }}
                                >
                                    <CloseIcon className='p-0.5' />
                                </IconButton>
                            </Toolbar>
                        </AppBar>
                        <Box
                            className="h-[calc(100%-30px)] bg-white border-collapse"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    width: '100%',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0,
                                }}
                            >
                                {applicationRows.length > 0 && (
                                    <Accordion
                                        disableGutters
                                        elevation={0}
                                        expanded={expandedSections.application}
                                        onChange={handleAccordionChange('application')}
                                        sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 } }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ minHeight: 36, px: 1.5, backgroundColor: '#f8fbff' }}
                                        >
                                            <Typography fontSize={12} fontWeight={600}>Applications</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <DataGridApplication
                                                rows={applicationRows}
                                                onRowsChange={setApplicationRows}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                {restfulApiRows.length > 0 && (
                                    <Accordion
                                        disableGutters
                                        elevation={0}
                                        expanded={expandedSections.restfulApi}
                                        onChange={handleAccordionChange('restfulApi')}
                                        sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 } }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ minHeight: 36, px: 1.5, backgroundColor: '#f8fbff' }}
                                        >
                                            <Typography fontSize={12} fontWeight={600}>RESTful-API</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <DataGridRESTfulAPI
                                                rows={restfulApiRows}
                                                onRowsChange={setRestfulApiRows}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                {dataRows.length > 0 && (
                                    <Accordion
                                        disableGutters
                                        elevation={0}
                                        expanded={expandedSections.data}
                                        onChange={handleAccordionChange('data')}
                                        sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 } }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ minHeight: 36, px: 1.5, backgroundColor: '#f8fbff' }}
                                        >
                                            <Typography fontSize={12} fontWeight={600}>Data</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <DataGridData
                                                rows={dataRows}
                                                onRowsChange={setDataRows}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                                {/* {selectedProfileSimulate ? (
                                    <DataGridPolicySimulator
                                        rows={rows}
                                        setRows={setRows}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: 2,
                                        }}
                                    >
                                        <Typography fontSize={13} color="text.secondary" textAlign="center">
                                            Please select profile before loading policy request.
                                        </Typography>
                                    </Box>
                                )} */}
                            </Box>
                        </Box>
                    </Panel>
                </PanelGroup>
            </Panel>
            <UserProfileManagement
                open={openManagement}
                setOpen={setOpenManagement}
                project={project}
                profiles={simulatorProfiles}
                onSaveProfile={(profile) => onSaveProfile(profile)}
                usedSubjectAttributes={memoizedAttributes}
                onAddNewProfile={onAddNewProfile}
                onCloneProfile={(profile) => onCloneProfile(profile)}
                selectedEditProfile={selectedEditProfile}
                initialProfileId={selectedEditProfile?.id}
            />
        </>
    );
};

export default PolicySimulatorPanel;